import "@/App.css";
import { useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { WaitlistProvider } from "@/context/WaitlistContext";
import Navbar from "@/components/Navbar";
import Hero from "@/components/Hero";
import WaitlistModal from "@/components/WaitlistModal";

// Below-the-fold landing sections are code-split to shrink the critical bundle.
const ChatDemo = lazy(() => import("@/components/ChatDemo"));
const Features = lazy(() => import("@/components/Features"));
const CTASection = lazy(() => import("@/components/CTASection"));
const Footer = lazy(() => import("@/components/Footer"));

// Non-critical routes are code-split to keep the landing bundle lean.
const AdminPage = lazy(() => import("@/pages/Admin"));
const About = lazy(() => import("@/pages/About"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const Terms = lazy(() => import("@/pages/Terms"));
const Contact = lazy(() => import("@/pages/Contact"));
const Faq = lazy(() => import("@/pages/Faq"));
const Founder = lazy(() => import("@/pages/Founder"));

const SectionFallback = () => <div className="min-h-[40vh]" aria-hidden="true" />;

const Landing = () => (
  <WaitlistProvider>
    <div className="App" data-testid="app-root">
      <Navbar />
      <main>
        <Hero />
        <Suspense fallback={<SectionFallback />}>
          <ChatDemo />
          <Features />
          <CTASection />
        </Suspense>
      </main>
      <Suspense fallback={null}>
        <Footer />
      </Suspense>
      <WaitlistModal />
    </div>
  </WaitlistProvider>
);

const RouteFallback = () => (
  <div className="flex min-h-screen items-center justify-center bg-white" data-testid="route-loading">
    <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-[var(--jb-blue)]" />
  </div>
);

function RouteTracker() {
  const location = useLocation();
  useEffect(() => {
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("event", "page_view", {
        page_path: location.pathname + location.search,
        page_location: window.location.href,
        page_title: document.title,
      });
    }
  }, [location]);
  return null;
}

// Scrolls to top on navigation, or to a #hash target (retrying while
// lazy-loaded sections mount).
function ScrollManager() {
  const location = useLocation();
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.slice(1);
      let tries = 0;
      const tryScroll = () => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
          return;
        }
        if (tries < 40) {
          tries += 1;
          setTimeout(tryScroll, 100);
        }
      };
      tryScroll();
    } else {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }
  }, [location.pathname, location.hash]);
  return null;
}

function App() {
  return (
    <BrowserRouter>
      <RouteTracker />
      <ScrollManager />
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/" element={<Navigate to="/waitlist" replace />} />
          <Route path="/waitlist" element={<Landing />} />
          <Route path="/about" element={<About />} />
          <Route path="/founder/wungnaokui-awungshi" element={<Founder />} />
          <Route path="/founder" element={<Navigate to="/founder/wungnaokui-awungshi" replace />} />
          <Route path="/wungnaokui-awungshi" element={<Navigate to="/founder/wungnaokui-awungshi" replace />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/faq" element={<Faq />} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="*" element={<Navigate to="/waitlist" replace />} />
        </Routes>
      </Suspense>
      <Toaster position="top-center" richColors />
    </BrowserRouter>
  );
}

export default App;
